import React, { useState, useEffect } from 'react';
import styles from './TwoFactorSetup.module.css';
import { authService } from '../../../services/api';

const TwoFactorSetup = ({ isOpen, onClose, currentMethod = null }) => {
  const [selectedMethod, setSelectedMethod] = useState(currentMethod || null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // TOTP setup data
  const [totpData, setTotpData] = useState({
    secret: '',
    qrCode: '',
    manualEntryKey: ''
  });
  
  // Verification
  const [verificationCode, setVerificationCode] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [showVerification, setShowVerification] = useState(false);

  const twoFactorMethods = [
    {
      id: 'email',
      name: 'Email',
      description: 'Get codes via email',
      icon: 'fas fa-envelope',
      color: '#10B981'
    },
    {
      id: 'totp',
      name: 'Google Authenticator',
      description: 'Use authenticator app',
      icon: 'fas fa-mobile-alt',
      color: '#3B82F6'
    }
  ];

  useEffect(() => {
    if (isOpen && currentMethod) {
      setSelectedMethod(currentMethod);
      initializeSetup(currentMethod);
    } else if (isOpen) {
      // Reset state when opening fresh
      setSelectedMethod(null);
      setShowVerification(false);
      setVerificationCode('');
      setPhoneNumber('');
      setError('');
      setSuccess('');
    }
  }, [isOpen, currentMethod]);

  const handleMethodSelect = async (methodId) => {
    setSelectedMethod(methodId);
    setError('');
    setSuccess('');
    setShowVerification(false);
    setVerificationCode('');
    
    // Immediately initialize setup for the selected method
    await initializeSetup(methodId);
  };

  const initializeSetup = async (methodId) => {
    setIsLoading(true);
    setError('');
    
    try {
      if (methodId === 'totp') {
        const response = await authService.generateTOTPSecret();
        if (response.data.status === 'success') {
          setTotpData(response.data.data);
        }
      } else if (methodId === 'email') {
        // Email is ready to send code
        setShowVerification(false);
      }
    } catch (err) {
      setError('Failed to initialize 2FA setup. Please try again.');
      console.error('2FA initialization error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const sendEmailCode = async () => {
    setIsLoading(true);
    try {
      const response = await authService.sendEmailTwoFactorCode();
      if (response.data.status === 'success') {
        setSuccess('Verification code sent to your email');
        setShowVerification(true);
      }
    } catch (err) {
      setError('Failed to send email code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };



  const verifyCode = async () => {
    if (!verificationCode.trim()) {
      setError('Please enter the verification code');
      return;
    }
    
    setIsLoading(true);
    setError('');
    
    try {
      const response = await authService.verifyTwoFactorSetup(
        selectedMethod, 
        verificationCode, 
        selectedMethod === 'totp' ? totpData.secret : null
      );
      
      if (response.data.status === 'success') {
        setSuccess('2FA has been successfully enabled!');
        setTimeout(() => {
          onClose();
          window.location.reload(); // Refresh to update 2FA status
        }, 1500);
      }
    } catch (err) {
      setError('Invalid verification code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <h2>Set Up Two-Factor Authentication</h2>
          <button className={styles.closeButton} onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className={styles.modalContent}>
          {error && (
            <div className={styles.error}>
              <i className="fas fa-exclamation-circle"></i>
              {error}
            </div>
          )}
          
          {success && (
            <div className={styles.success}>
              <i className="fas fa-check-circle"></i>
              {success}
            </div>
          )}

          {/* Method Selection Row */}
          <div className={styles.methodsRow}>
            {twoFactorMethods.map((method) => (
              <div
                key={method.id}
                className={`${styles.methodCard} ${styles[method.id + 'Card']} ${selectedMethod === method.id ? styles.selected : ''}`}
                onClick={() => handleMethodSelect(method.id)}
              >
                <div className={`${styles.methodIcon} ${styles[method.id + 'Icon']}`}>
                  <i className={method.icon}></i>
                </div>
                <h3>{method.name}</h3>
                <p>{method.description}</p>
              </div>
            ))}
          </div>

          {/* Setup Content - shows immediately when method is selected */}
          {selectedMethod && (
            <div className={styles.setupContent}>
              
              {/* Email Setup */}
              {selectedMethod === 'email' && !showVerification && (
                <div className={styles.setupStep}>
                  <h3>Email Authentication</h3>
                  <p>We'll send a verification code to your registered email address.</p>
                  <button 
                    className={styles.primaryButton}
                    onClick={sendEmailCode}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Sending...' : 'Send Code to Email'}
                  </button>
                </div>
              )}

              {/* TOTP Setup */}
              {selectedMethod === 'totp' && !showVerification && (
                <div className={styles.setupStep}>
                  <h3>Scan QR Code</h3>
                  <p>Open your authenticator app and scan this QR code:</p>
                  
                  {totpData.qrCode && (
                    <div className={styles.qrSection}>
                      <div className={styles.qrContainer}>
                        <img src={totpData.qrCode} alt="2FA QR Code" className={styles.qrCode} />
                      </div>
                      
                      <div className={styles.manualEntry}>
                        <p>Can't scan? Enter this code manually:</p>
                        <div className={styles.secretKey}>
                          <code>{totpData.manualEntryKey}</code>
                          <button 
                            onClick={() => navigator.clipboard.writeText(totpData.manualEntryKey)}
                            className={styles.copyButton}
                            title="Copy to clipboard"
                          >
                            <i className="fas fa-copy"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <button 
                    className={styles.primaryButton}
                    onClick={() => setShowVerification(true)}
                  >
                    I've Added the Account
                  </button>
                </div>
              )}



              {/* Verification Step */}
              {showVerification && (
                <div className={styles.setupStep}>
                  <h3>Enter Verification Code</h3>
                  <p>
                    {selectedMethod === 'email' && 'Enter the code sent to your email'}
                    {selectedMethod === 'totp' && 'Enter the code from your Google Authenticator app'}
                  </p>
                  
                  <div className={styles.codeInput}>
                    <input
                      type="text"
                      placeholder="000000"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className={styles.input}
                      maxLength="6"
                    />
                  </div>
                  
                  {selectedMethod === 'email' && (
                    <button 
                      className={styles.resendButton}
                      onClick={sendEmailCode}
                      disabled={isLoading}
                    >
                      Resend Code
                    </button>
                  )}

                  <button 
                    className={styles.primaryButton}
                    onClick={verifyCode}
                    disabled={isLoading || verificationCode.length !== 6}
                  >
                    {isLoading ? 'Verifying...' : 'Verify & Enable 2FA'}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Show instruction when no method selected */}
          {!selectedMethod && (
            <div className={styles.instruction}>
              <p>Choose a method above to get started</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TwoFactorSetup;
 